import { addYears, addDays, differenceInCalendarDays } from 'date-fns';
import { cezaEksilt } from '../utils/cezaHesaplamalari';

/**
 * Zamanaşımı hesabı için TCK 72 uyarınca süre aritmetiği:
 *   "Bir gün, yirmidört saat; bir ay, otuz gündür. Yıl, resmî takvime göre hesap edilir."
 * Yani YIL bileşeni takvim yılı (artık yıllar dahil) olarak; AY bileşeni 30 gün;
 * GÜN bileşeni 1 gün olarak eklenir. Bu, cezaEksilt'in 30-günlük ay tabanıyla da tutarlıdır.
 * (Tam yıllık sürelerde — ay=0, gün=0 — sonuç saf takvim yılıdır.)
 */
export function addPeriod(tarih, sure) {
  const yilEklenmis = addYears(new Date(tarih), sure.year || 0);
  return addDays(yilEklenmis, (sure.month || 0) * 30 + (sure.day || 0));
}

/**
 * TCK 66/2: Kanuni süreden, failin yaşına göre KALACAK kesri uygular.
 * keepNum/keepDen = kalan oran (12–15 → 1/2, 15–18 → 2/3, yetişkin → 1/1).
 * cezaEksilt çıkarma yaptığından, tümleyen (keepDen − keepNum)/keepDen kadar eksiltilir.
 */
export function keepFractionPeriod(sure, keepNum, keepDen) {
  return cezaEksilt(sure, {
    rate: { numerator: keepDen - keepNum, denominator: keepDen },
  });
}

/** Sürenin yarısı — TCK 67/4 (kesilmede en fazla yarısı kadar uzama) tabanı. */
export function halfPeriod(sure) {
  return cezaEksilt(sure, { rate: { numerator: 1, denominator: 2 } });
}

/**
 * İki süre objesini TCK 72 (30 günlük ay, 12 aylık yıl) taşımasıyla toplar.
 * Üst sınırı (uygulanan süre × 1,5 = A + A/2) tek bir normalize süre olarak hesaplamak
 * için kullanılır; böylece A ve A/2'yi ayrı ayrı tarihe eklemekten doğan (360≠365) kayma olmaz.
 */
export function topla(a, b) {
  let day = (a.day || 0) + (b.day || 0);
  let month = (a.month || 0) + (b.month || 0);
  let year = (a.year || 0) + (b.year || 0);
  month += Math.floor(day / 30);
  day %= 30;
  year += Math.floor(month / 12);
  month %= 12;
  return { year, month, day };
}

/** İki tarih arası gün sayısı — durmanın saati durdurduğu takvim uzunluğu. */
export function durmaGunSayisi(baslangic, bitis) {
  const g = differenceInCalendarDays(new Date(bitis), new Date(baslangic));
  return g > 0 ? g : 0;
}

/**
 * Çakışan/bitişik durma aralıklarını birleştirir (overlap tek sayılsın).
 * @returns {Array<{ start: Date, end: Date }>} başlangıca göre sıralı, birleştirilmiş aralıklar
 */
export function mergeDurmalar(durmalar) {
  const araliklar = (durmalar || [])
    .map((d) => ({ start: new Date(d.baslangic), end: new Date(d.bitis) }))
    .filter((d) => d.end > d.start) // sıfır/negatif uzunlukları ele
    .sort((a, b) => a.start - b.start);

  const merged = [];
  for (const a of araliklar) {
    const son = merged[merged.length - 1];
    if (son && a.start <= son.end) {
      if (a.end > son.end) son.end = a.end; // örtüşeni genişlet
    } else {
      merged.push({ start: a.start, end: a.end });
    }
  }
  return merged;
}

/**
 * TCK 67/1 durma: Süre-sonu tarihine durmaları "saati durdur" mantığıyla uygular.
 * Yalnızca başlangıcı [windowStart, o anki süre-sonu) içinde olan durma, süreyi kendi
 * takvim uzunluğu kadar öteler. Başlangıca göre sıralı işlenir; bir durmanın eklediği
 * uzama, sonraki durmanın hâlâ "canlı" olup olmadığını etkiler.
 * windowStart, durmanın hangi "koşuya" (run) ait olduğunu sınırlar: bir kesme sonrası
 * yeniden başlayan koşu, kesmeden ÖNCE başlamış durmalardan etkilenmez.
 *
 * @returns {{ deadline: Date, uygulananlar: Array<{ start: Date, end: Date, gun: number }> }}
 */
export function applyDurma(sureSonu, windowStart, mergedDurmalar) {
  let deadline = new Date(sureSonu);
  const ws = new Date(windowStart);
  const uygulananlar = [];

  for (const d of mergedDurmalar) {
    if (d.start >= ws && d.start < deadline) {
      const gun = durmaGunSayisi(d.start, d.end);
      if (gun > 0) {
        deadline = addDays(deadline, gun);
        uygulananlar.push({ start: d.start, end: d.end, gun });
      }
    }
  }

  return { deadline, uygulananlar };
}
